import { T, Var, useGT } from 'gt-react';
import { CheckIcon, ScissorsIcon, WarningIcon } from '@phosphor-icons/react';
import { TableCell, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { formatCurrency } from '@/utils/formatters';
import {
  getSplitGroup,
  getSplitMemberPosition,
  splitGroupBalances,
  type SplitGroups,
} from './splitLineGroups';
import { UndoSplitButton } from './UndoSplitButton';

/** "1/2"-style marker under the line number of a split part. */
export function SplitMemberMarker({
  splitGroups,
  lineItemId,
}: {
  splitGroups: SplitGroups;
  lineItemId: string;
}) {
  const position = getSplitMemberPosition(splitGroups, lineItemId);
  if (!position) return null;
  return (
    <div className="text-[9px] font-normal text-violet-600">
      {position.index}/{position.count}
    </div>
  );
}

function SumComparison({ sum, original }: { sum: string; original: string }) {
  const differs = sum !== original;
  return (
    <div className="space-y-0.5 text-xs leading-tight">
      <div className="flex items-baseline justify-end gap-1.5">
        <span className="text-muted-foreground">
          <T context="label: sum of the split parts">Sum</T>:
        </span>
        <span className="tabular-nums font-medium">{sum}</span>
      </div>
      <div className="flex items-baseline justify-end gap-1.5">
        <span className="text-muted-foreground">
          <T context="label: the invoice line before it was split">Inv</T>:
        </span>
        <span
          className={cn(
            'tabular-nums',
            differs && 'text-yellow-700 font-semibold',
          )}
        >
          {original}
        </span>
      </div>
    </div>
  );
}

/**
 * Σ row under the last part of a split line: the parts' summed quantity and
 * total against the original invoice line, how many parts are matched, and
 * (for users who can split) the action that undoes the split.
 */
export function SplitGroupSubtotalRow({
  invoiceId,
  splitGroups,
  lineItemId,
  currency,
  isLineMatched,
  showMatching,
  showActionsColumn,
}: {
  invoiceId: string;
  splitGroups: SplitGroups;
  lineItemId: string;
  currency: string;
  isLineMatched: (lineItemId: string) => boolean;
  showMatching: boolean;
  showActionsColumn: boolean;
}) {
  const gt = useGT();
  const position = getSplitMemberPosition(splitGroups, lineItemId);
  const group = getSplitGroup(splitGroups, lineItemId);
  if (!group || !position?.isLast) return null;

  const money = (value: string) => formatCurrency(Number(value), { currency });
  const matchedCount = group.memberIds.filter(isLineMatched).length;
  const allMatched = matchedCount === group.memberIds.length;
  const balanced = splitGroupBalances(group);
  const lineLabel =
    group.originalLineNumber == null
      ? gt('Split line ({count} parts)', {
          count: group.memberIds.length,
          context: 'AP invoice: subtotal row label for a split line',
        })
      : gt('Split from line {n} ({count} parts)', {
          n: group.originalLineNumber,
          count: group.memberIds.length,
          context: 'AP invoice: subtotal row label for a split line',
        });

  return (
    <TableRow className="bg-muted/40 border-l-4 border-l-violet-400">
      <TableCell className="text-center text-xs font-semibold text-muted-foreground">
        Σ
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
          <ScissorsIcon className="h-3 w-3" />
          <span>{lineLabel}</span>
        </div>
      </TableCell>
      <TableCell>
        <div className="flex items-baseline justify-end gap-1.5 text-xs">
          <span className="text-muted-foreground">
            <T context="label: sum of the split parts">Sum</T>:
          </span>
          <span className="tabular-nums font-medium">
            {group.summedQuantity}
          </span>
        </div>
      </TableCell>
      <TableCell />
      <TableCell>
        <SumComparison
          sum={money(group.summedTotal)}
          original={money(group.originalTotalPrice)}
        />
      </TableCell>
      {showMatching && (
        <TableCell>
          <div
            className={cn(
              'inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs font-medium',
              allMatched && balanced
                ? 'border-green-500 bg-green-50'
                : 'border-amber-400 bg-amber-50',
            )}
          >
            {allMatched && balanced ? (
              <CheckIcon className="h-3 w-3 text-green-600" />
            ) : (
              <WarningIcon className="h-3 w-3 text-amber-500" />
            )}
            <T context="AP invoice: how many parts of a split line are matched">
              <Var>{matchedCount}</Var>/<Var>{group.memberIds.length}</Var>{' '}
              parts matched
            </T>
          </div>
        </TableCell>
      )}
      {showMatching && <TableCell />}
      {showActionsColumn && (
        <TableCell>
          <UndoSplitButton
            invoiceId={invoiceId}
            lineItemId={lineItemId}
            partCount={group.memberIds.length}
            originalTotalPrice={group.originalTotalPrice}
            currency={currency}
          />
        </TableCell>
      )}
    </TableRow>
  );
}
